import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Plus, Save, Pencil, X } from "lucide-react";
import { api, imgUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const AdminFlavors = ({ token }) => {
  const [flavors, setFlavors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // flavor id or "new"
  const [form, setForm] = useState({ name: "", description: "" });
  const inputs = useRef({});
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    const res = await api.get("/admin/flavors", authHeader);
    setFlavors(res.data.flavors);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load flavors."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => { setEditing("new"); setForm({ name: "", description: "" }); };
  const startEdit = (f) => { setEditing(f.id); setForm({ name: f.name, description: f.description }); };
  const cancel = () => { setEditing(null); setForm({ name: "", description: "" }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admin/flavors", form, authHeader);
        toast.success("Flavor added!");
      } else {
        await api.put(`/admin/flavors/${editing}`, form, authHeader);
        toast.success("Flavor updated!");
      }
      cancel();
      await load();
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this flavor? It will no longer appear on your site.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/flavors/${id}`, authHeader);
      toast.success("Flavor removed.");
      await load();
    } catch {
      toast.error("Could not remove flavor.");
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/flavors/${id}/images`, fd, authHeader);
      toast.success("Photo added!");
      await load();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteImage = async (id, imageId) => {
    setBusy(true);
    try {
      await api.delete(`/admin/flavors/${id}/images/${imageId}`, authHeader);
      await load();
    } catch {
      toast.error("Could not remove photo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-2xl font-bold text-ink">Flavors</h3>
          <p className="text-sm text-ashydark">Add, edit, remove flavors and manage photos. Uploaded photos override the default; the first is the main image.</p>
        </div>
        <Button data-testid="add-flavor-button" onClick={startNew} className="rounded-full bg-cocoa text-white hover:bg-cocoadark"><Plus className="mr-1.5 h-4 w-4" /> Add flavor</Button>
      </div>

      {editing === "new" && (
        <FlavorForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} isNew />
      )}

      <div className="space-y-4">
        {flavors.map((flavor) => (
          <div key={flavor.id} data-testid={`admin-flavor-${flavor.id}`} className="rounded-xl border border-ashy/20 bg-white p-4">
            {editing === flavor.id ? (
              <FlavorForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} />
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-semibold text-ink">{flavor.name}</h4>
                  <p className="text-sm text-ashydark">{flavor.description}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="outline" data-testid={`edit-flavor-${flavor.id}`} onClick={() => startEdit(flavor)} className="rounded-full border-ashy/40"><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" data-testid={`delete-flavor-${flavor.id}`} onClick={() => remove(flavor.id)} className="rounded-full border-ashy/40 text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {flavor.images.length === 0 && (
                <div className="relative h-20 w-20 overflow-hidden rounded-lg opacity-60">
                  <img src={imgUrl(flavor.default_image)} alt="default" className="h-full w-full object-cover" />
                  <span className="absolute bottom-0 w-full bg-black/50 py-0.5 text-center text-[10px] text-white">Default</span>
                </div>
              )}
              {flavor.images.map((img, i) => (
                <div key={img.id} className="group relative h-20 w-20 overflow-hidden rounded-lg border border-ashy/30">
                  <img src={imgUrl(img.url)} alt="" className="h-full w-full object-cover" />
                  {i === 0 && <span className="absolute left-0 top-0 bg-cocoa px-1.5 py-0.5 text-[10px] text-white">Main</span>}
                  <button data-testid={`delete-photo-${flavor.id}-${i}`} disabled={busy} onClick={() => deleteImage(flavor.id, img.id)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-destructive opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <input type="file" accept="image/*" hidden ref={(el) => (inputs.current[flavor.id] = el)} data-testid={`flavor-file-input-${flavor.id}`} onChange={(e) => { uploadImage(flavor.id, e.target.files[0]); e.target.value = ""; }} />
              <button data-testid={`add-photo-${flavor.id}`} disabled={busy} onClick={() => inputs.current[flavor.id]?.click()} className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-ashy/50 text-ashydark transition-colors hover:border-cocoa hover:text-cocoa">
                <ImagePlus className="h-5 w-5" />
                <span className="text-[10px]">Add</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FlavorForm = ({ form, setForm, onSave, onCancel, busy, isNew }) => (
  <div className="rounded-lg border border-cocoa/30 bg-babybluelight/40 p-4" data-testid={isNew ? "new-flavor-form" : "edit-flavor-form"}>
    <div className="grid gap-3">
      <Input data-testid="flavor-name-input" placeholder="Flavor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" />
      <Textarea data-testid="flavor-desc-input" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white" />
      <div className="flex gap-2">
        <Button data-testid="save-flavor-button" onClick={onSave} disabled={busy} className="rounded-full bg-cocoa text-white hover:bg-cocoadark"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
        <Button variant="ghost" onClick={onCancel} className="rounded-full"><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
      </div>
    </div>
  </div>
);
